import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {UserDetailsEditButtons} from '@/features/users/components/user-details-edit-buttons/UserDetailsEditButtons'
import {UserAvailabilityView} from '@/features/users-availability/components/user-availability-view/UserAvailabilityView'
import {UserAvailabilitySkeletonView} from '@/features/users-availability/components/user-availability-view/UserAvailabilitySkeletonView'
import {UserAvailabilityForm} from '@/features/users-availability/components/user-availability-form/UserAvailabilityForm'
import {UserAvailabilityFormProvider} from '@/features/users-availability/components/user-availability-form-provider/UserAvailabilityFormProvider'
import {defaultEmptyAvailability, UserAvailability} from '@/features/users-availability/types'
import {useUserEditModeView} from '@/features/users/hooks/useUserEditModeView'
import {useGetUserAvailability} from '@/features/users-availability/queries/useGetUserAvailability'
import {useUpdateUserAvailability} from '@/features/users-availability/queries/useUpdateUserAvailability'
import {useTranslation} from '@/translations/i18n'
import {FC, useEffect, useRef, useCallback, useMemo} from 'react'
import {useFormContext, FieldErrors} from 'react-hook-form'
import {toast} from '@/utilities/toast/toast'
import {AvailabilityFormValidationSchema} from '@/features/users-availability/utils'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'
import {UserBase} from '@/features/users/types'
import {useUserDetailsStore} from '@/features/users/store/userDetailsStore'

interface UserDetailsAvailabilitySectionProps {
    user: UserBase
}

export const UserDetailsAvailabilitySection: FC<UserDetailsAvailabilitySectionProps> = ({user}) => {
    const {t} = useTranslation()
    const {isEditModeEnabled, isDisabledView} = useUserEditModeView('availability')
    const queryClient = useQueryClient()
    const setEditingFieldsGroup = useUserDetailsStore(state => state.setEditingFieldsGroup)

    const {data: availability = defaultEmptyAvailability, isLoading} = useGetUserAvailability({
        urlParams: {userId: user.id}
    })

    const queryKey = useMemo(() => [QUERY_KEYS.USER_AVAILABILITY, user.id], [user.id])

    const {mutate: updateAvailability, isPending} = useUpdateUserAvailability({
        urlParams: {userId: user.id},
        options: {
            onMutate: async newData => {
                await queryClient.cancelQueries({queryKey})
                queryClient.setQueryData(queryKey, newData)
            },
            onSuccess: () => {
                queryClient.cancelQueries({queryKey})
                queryClient.invalidateQueries({queryKey})
                queryClient.cancelQueries({queryKey: [QUERY_KEYS.AGENDA_AVAILABILITIES]})
                queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_AVAILABILITIES]})
                toast.success(t('commons:update_success'))
                setEditingFieldsGroup(null)
            },
            onError: () => {
                queryClient.invalidateQueries({queryKey})
                toast.error(t('errors:default'))
            }
        }
    })

    const handleSubmit = useCallback(
        (data: AvailabilityFormValidationSchema) => {
            updateAvailability(data)
        },
        [updateAvailability]
    )

    if (isLoading) {
        return (
            <DetailsFieldsSection
                title={t('users:availability')}
                isDisabled={isDisabledView}
                headingEndSlot={isEditModeEnabled ? <div style={{width: 159, height: 32}} /> : undefined}
            >
                <UserAvailabilitySkeletonView />
            </DetailsFieldsSection>
        )
    }

    return (
        <UserAvailabilityFormProvider defaultValues={availability}>
            <UserAvailabilityContent
                isEditModeEnabled={isEditModeEnabled}
                isDisabledView={isDisabledView}
                availability={availability}
                onSubmit={handleSubmit}
                isSubmitting={isPending}
            />
        </UserAvailabilityFormProvider>
    )
}

interface UserAvailabilityContentProps {
    isEditModeEnabled: boolean
    isDisabledView: boolean
    availability: UserAvailability
    onSubmit: (data: AvailabilityFormValidationSchema) => void
    isSubmitting: boolean
}

const UserAvailabilityContent: FC<UserAvailabilityContentProps> = ({
    isEditModeEnabled,
    isDisabledView,
    availability,
    onSubmit,
    isSubmitting
}) => {
    const {t} = useTranslation()
    const form = useFormContext<AvailabilityFormValidationSchema>()
    const previousEditMode = useRef(isEditModeEnabled)
    const availabilityRef = useRef(availability)

    useEffect(() => {
        if (isEditModeEnabled && !previousEditMode.current) {
            const currentJson = JSON.stringify(availability)
            const prevJson = JSON.stringify(availabilityRef.current)

            if (currentJson !== prevJson) {
                form.reset(availability)
                availabilityRef.current = availability
            }
        }

        previousEditMode.current = isEditModeEnabled
    }, [isEditModeEnabled, availability, form])

    const handleFormSubmit = useCallback(
        (data: AvailabilityFormValidationSchema) => {
            if (Object.keys(form.formState.errors).length === 0) {
                onSubmit(data)
            } else {
                toast.error(t('errors:form_default'))
            }
        },
        [form.formState.errors, onSubmit, t]
    )

    const handleFormError = useCallback(
        (errors: FieldErrors<AvailabilityFormValidationSchema>) => {
            if (Object.keys(errors).length > 0) {
                toast.error(t('errors:form_default'))
            }
        },
        [t]
    )

    const handleCancel = useCallback(() => {
        form.reset(availabilityRef.current)
    }, [form])

    const isConfirmDisabled = !form.formState.isDirty || Object.keys(form.formState.errors).length > 0 || isSubmitting

    return (
        <DetailsFieldsSection
            title={t('users:availability')}
            isDisabled={isDisabledView}
            headingEndSlot={
                <UserDetailsEditButtons
                    groupName="availability"
                    isConfirmDisabled={isConfirmDisabled}
                    onCancel={handleCancel}
                    onConfirm={form.handleSubmit(handleFormSubmit, handleFormError)}
                />
            }
        >
            {isEditModeEnabled ? <UserAvailabilityForm /> : <UserAvailabilityView availability={availability} />}
        </DetailsFieldsSection>
    )
}
